import * as ReactHelmetAsync from 'react-helmet-async';
const Helmet = ReactHelmetAsync.Helmet || (ReactHelmetAsync as any).default?.Helmet;
import { 
  ChevronDown, 
  Award, 
  Navigation, 
  Map,
  TrendingUp,
  Compass,
  Mail,
  Clock
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useHomeData } from '@/hooks/useHomeData';
import { resolveImagePath } from '@/utils/imageMap';
import { Navigation as NavComponent } from '@/components/layout/Navigation';
import { HeroGallery } from '@/components/ui/HeroGallery';
import { Copyright } from '@/components/ui/Copyright';

// Icon mapping for dynamic icon selection
const iconMap = {
  award: Award,
  navigation: Navigation,
  map: Map,
  'trending-up': TrendingUp,
  compass: Compass,
  clock: Clock,
};

export function Index() {
  const { homeData } = useHomeData();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Prepare hero gallery images
  const heroGalleryImages = [
    {
      src: resolveImagePath(homeData.hero.image), // Current airplane hero image first
      alt: homeData.hero.imageAlt,
      caption: "Our beautiful 1954 Cessna 170B - N3472C"
    },
    {
      src: resolveImagePath("/assets/images/gallery/letsgo.jpg"), // Most recent image second
      alt: "Ready for takeoff",
      caption: "Ready for your tailwheel adventure"
    },
    // Add more gallery images
    ...homeData.about.gallery.map((img) => ({
      src: resolveImagePath(img.src),
      alt: img.alt,
      caption: img.caption
    }))
  ];

  // Smooth scroll function
  const scrollToSection = (sectionId: string) => {
    if (typeof window !== 'undefined') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };



  return (
    <>
      <Helmet>
        <title>Tailwheel Flight Training & Endorsement | Flight School | Total Tailwheel</title>
        <meta
          name="description"
          content="Professional tailwheel flight school offering endorsement training near Boston, MA. Learn in our restored 1954 Cessna 170B at Minute Man Air Field in Stow."
        />
        <meta name="keywords" content="tailwheel flight school, tailwheel training, flight instruction, tailwheel endorsement, conventional gear training, flight school Massachusetts, Cessna 170, Stow MA" />
        <meta property="og:title" content="Tailwheel Flight School & Training | Professional Flight Instruction" />
        <meta property="og:description" content="Professional tailwheel flight school offering endorsement training. Learn in our restored 1954 Cessna 170B at Minute Man Air Field in Stow, MA." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://totaltailwheel.com" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tailwheel Flight School & Training | Professional Flight Instruction" />
        <meta name="twitter:description" content="Professional tailwheel flight school offering endorsement training. Learn in our restored 1954 Cessna 170B." />
        <link rel="canonical" href="https://totaltailwheel.com" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Total Tailwheel, LLC",
            "description": "Professional tailwheel flight school offering endorsement training and lessons in Massachusetts",
            "url": "https://totaltailwheel.com",
            "telephone": "+1-XXX-XXX-XXXX",
            "email": "kevin@totaltailwheel.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Minute Man Air Field",
              "addressLocality": "Stow",
              "addressRegion": "MA",
              "postalCode": "01775",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "42.4615211",
              "longitude": "-71.5168457"
            },
            "openingHours": "Mo-Su 08:00-18:00",
            "priceRange": "$80-$220",
            "serviceArea": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": "42.4615211",
                "longitude": "-71.5168457"
              },
              "geoRadius": "50000"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Flight School Services",
              "itemListElement": [
                {
                  "@type": "Course",
                  "name": "Tailwheel Endorsement Training",
                  "description": "Learn the fundamentals of conventional gear aircraft operation",
                  "provider": {
                    "@type": "Organization",
                    "name": "Total Tailwheel, LLC"
                  },
                  "offers": {
                    "@type": "Offer",
                    "price": "300",
                    "priceCurrency": "USD",
                    "description": "$220/hr aircraft + $80/hr instruction"
                  }
                },
                {
                  "@type": "Course",
                  "name": "Instrument Flight Training",
                  "description": "Advanced instrument flying training in tailwheel aircraft",
                  "provider": {
                    "@type": "Organization",
                    "name": "Total Tailwheel, LLC"
                  }
                },
                {
                  "@type": "Course",
                  "name": "Commercial Pilot Training",
                  "description": "Commercial pilot certification training",
                  "provider": {
                    "@type": "Organization",
                    "name": "Total Tailwheel, LLC"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <NavComponent />
        
        {/* Hero Section */}
        <div className="relative flex items-center bg-gradient-to-b from-white to-gray-100 pt-16 pb-8">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col-reverse md:flex-row items-center">
              <div className="w-full md:w-1/2 text-center md:text-left mt-6 md:mt-0 md:pr-8">
                <h1 className="text-xl md:text-2xl text-foreground/60 mt-2 font-light">
                  {homeData.hero.subtitle}
                </h1>
                <div className="h-1 w-24 bg-secondary mx-auto md:mx-0 my-4"></div>
                <p 
                  className="text-lg text-foreground/80 mt-2 max-w-lg mx-auto md:mx-0"
                  dangerouslySetInnerHTML={{ __html: homeData.hero.description.replace(/\*\*(.*?)\*\*/g, '<span class="font-medium">$1</span>') }}
                />
                
                {/* Contact CTA */}
                <div className="mt-4 flex items-center justify-center md:justify-start">
                  <Link
                    to="/inquire"
                    className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center gap-2"
                  >
                    <Mail size={20} />
                    Contact Us
                  </Link>
                </div>
              </div>
              
              <div className="w-full md:w-1/2 flex justify-center">
                <HeroGallery 
                  images={heroGalleryImages}
                  autoAdvanceInterval={6000}
                  className="max-w-full"
                />
              </div>
            </div>
            
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 animate-bounce">
              <button
                onClick={() => scrollToSection('about')}
                className="text-primary/60 hover:text-primary transition-colors"
                aria-label="Scroll down"
              >
                <ChevronDown size={32} />
              </button>
            </div>
          </div>
        </div>

        {/* About Section */}
        <section id="about" className="pt-20 pb-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-md">
              <p 
                className="text-lg text-foreground/80 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: homeData.about.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                }}
              />
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-md">
              <h2 className="text-3xl font-medium text-primary text-center md:text-left mb-8">
                {homeData.features.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {homeData.features.items.map((feature, index) => {
                  const IconComponent = iconMap[feature.icon as keyof typeof iconMap];
                  return (
                    <div key={index} className="p-6 rounded-lg border border-secondary/30">
                      <div className="flex items-start mb-4">
                        {IconComponent && <IconComponent className="text-primary mr-4 mt-1" size={24} />}
                        <h3 className="text-xl font-medium text-primary">{feature.title}</h3>
                      </div>
                      <p 
                        className="text-foreground/80"
                        dangerouslySetInnerHTML={{ 
                          __html: feature.description.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-blue-500 hover:text-blue-600 transition-colors">$1</a>') 
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Equipment Section */}
        <section id="equipment" className="py-16 bg-secondary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-md">
              <h2 className="text-3xl font-medium text-primary text-center md:text-left mb-8">
                {homeData.equipment.title}
              </h2>
              <div className="space-y-8">
                {homeData.equipment.items.map((item, index) => (
                  <div key={index} className="p-6 rounded-lg border border-secondary/30">
                    <h3 className="text-xl font-medium text-primary mb-2">{item.title}</h3>
                    <p className="text-foreground/80">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Training Section */}
        <section id="training" className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-md">
              <h2 className="text-3xl font-medium text-primary text-center md:text-left mb-8">
                {homeData.training.title}
              </h2>
              <p className="text-lg text-foreground/80 mb-6">{homeData.training.description}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {homeData.training.options.map((option, index) => {
                  const IconComponent = iconMap[option.icon as keyof typeof iconMap];
                  return (
                    <Link
                      key={index}
                      to={(option as any).path || '/training'}
                      className="flex items-center gap-3 bg-primary/5 border border-primary/20 px-5 py-4 rounded-lg hover:bg-primary/10 transition-colors group"
                    >
                      {IconComponent && <IconComponent size={22} className="text-primary shrink-0" />}
                      <span className="text-primary font-medium group-hover:text-primary/80">{option.text}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>



        {/* Location Section */}
        <section id="location" className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-medium text-primary text-center mb-8">
                {homeData.location.title}
              </h2>
              <div className="text-center mb-8">
                <p className="text-lg text-foreground/80">
                  {homeData.location.primaryDescription}
                </p>
                
                {homeData.location.showHangarImage && (
                  <div className="mt-6 flex flex-col items-center">
                    <p className="text-sm text-primary/70 mb-3">Hangar Location on Airfield</p>
                    <button
                      onClick={() => setLightboxOpen(true)}
                      className="hover:opacity-80 transition-opacity"
                      title="Click to view full-size hangar directions"
                    >
                      <img
                        src={resolveImagePath('/images/hangar-directions-thumb.jpg')}
                        alt="Hangar directions on airfield map"
                        className="rounded-lg shadow-md border max-w-xs w-full"
                      />
                    </button>
                    <p className="text-xs text-foreground/60 mt-2">Click to enlarge</p>
                  </div>
                )}

                <p className="text-lg text-foreground/80 mt-6">
                  {homeData.location.accessDescription}
                </p>
              </div>
              <div className="rounded-lg overflow-hidden shadow-md">
                <iframe
                  src={homeData.location.mapEmbedUrl}
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Minute Man Air Field Map"
                ></iframe>
              </div>
            </div>
          </div>
        </section>

        {/* Copyright Section */}
        <div className="bg-secondary/10 py-8">
          <div className="container mx-auto px-4">
            <Copyright showSeparator={true} />
          </div>
        </div>

      </div>

      {/* Lightbox for hangar directions */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-lg font-bold"
            >
              ✕ Close
            </button>
            <img
              src={resolveImagePath('/images/hangar-directions.jpg')}
              alt="Hangar directions on airfield map - full size"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
}